import React from 'react'
import { motion } from 'framer-motion'
import { useParams } from 'react-router-dom'
import { Music, Calendar, Star, MapPin, Play, Share2, Shield, Globe, Instagram, Youtube } from 'lucide-react'

const ProfilePage = () => {
  const { id } = useParams()
  const artist = {
    id,
    name: 'Sarah Mitchell',
    genres: ['Indie Folk', 'Acoustic'],
    rating: 4.9,
    followers: 1234,
    location: 'Wichita, KS',
    image: 'https://images.pexels.com/photos/1587927/pexels-photo-1587927.jpeg?auto=compress&cs=tinysrgb&w=1200',
    bio: 'Wichita-born singer-songwriter with a soulful voice and heartfelt lyrics that capture the essence of Midwest life. Performing across Kansas with originals and select covers.',
    booking: {
      available: true,
      rate: '$500–1000/show',
      contact: 'booking@sarahmitchellmusic.com'
    },
    socials: {
      website: '#',
      instagram: '#',
      youtube: '#'
    },
    media: [
      'https://images.pexels.com/photos/1105666/pexels-photo-1105666.jpeg?auto=compress&cs=tinysrgb&w=800',
      'https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg?auto=compress&cs=tinysrgb&w=800',
      'https://images.pexels.com/photos/1540406/pexels-photo-1540406.jpeg?auto=compress&cs=tinysrgb&w=800'
    ],
    upcoming: [
      { id: 'e1', title: 'Acoustic Evening', venue: 'The Cotillion', date: 'Jul 18, 8:00 PM' },
      { id: 'e2', title: 'Indie Night', venue: 'Kirby\'s', date: 'Jul 24, 9:00 PM' },
    ]
  }

  return (
    <div className="pt-16 lg:pt-20 min-h-screen">
      {/* Hero */}
      <section className="section-padding pt-10 pb-6">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-[320px,1fr] gap-8 items-start">
            {/* Avatar / Stats */}
            <motion.div className="glass-card overflow-hidden" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="h-80 overflow-hidden">
                <img src={artist.image} alt={artist.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center gap-2 text-white">
                  <Star size={18} className="text-yellow-400 fill-current" />
                  <span className="font-medium">{artist.rating}</span>
                </div>
                <div className="text-white/70">{artist.followers.toLocaleString()} followers</div>
                <div className="flex flex-wrap gap-2">
                  {artist.genres.map((g) => (
                    <span key={g} className="bg-white/10 text-white/80 text-xs px-3 py-1 rounded-full">{g}</span>
                  ))}
                </div>
                {artist.booking.available && (
                  <div className="bg-le-accent/10 border border-le-accent/30 rounded-xl p-4">
                    <div className="text-white font-semibold mb-1">Available for Booking</div>
                    <div className="text-white/80 text-sm">Rate: {artist.booking.rate}</div>
                    <div className="text-white/60 text-sm mt-1">Contact: {artist.booking.contact}</div>
                  </div>
                )}
                <div className="flex gap-2">
                  <button className="btn-primary flex-1">Follow</button>
                  <button className="btn-secondary flex-1">Book</button>
                </div>
              </div>
            </motion.div>

            {/* Main */}
            <motion.div className="space-y-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}>
              <div className="glass-card p-6">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                  <div>
                    <h1 className="text-3xl font-display font-bold text-white">{artist.name}</h1>
                    <div className="flex items-center gap-3 text-white/70 mt-1">
                      <MapPin size={16} />
                      <span>{artist.location}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button className="btn-secondary flex items-center gap-2"><Share2 size={16} />Share</button>
                  </div>
                </div>
                <p className="text-white/80 leading-relaxed">{artist.bio}</p>
                <div className="flex items-center gap-3 mt-4 text-white/80">
                  {artist.socials.website && (
                    <a href={artist.socials.website} className="glass-card p-2 rounded-lg" aria-label="Website"><Globe size={16} /></a>
                  )}
                  {artist.socials.instagram && (
                    <a href={artist.socials.instagram} className="glass-card p-2 rounded-lg" aria-label="Instagram"><Instagram size={16} /></a>
                  )}
                  {artist.socials.youtube && (
                    <a href={artist.socials.youtube} className="glass-card p-2 rounded-lg" aria-label="YouTube"><Youtube size={16} /></a>
                  )}
                </div>
              </div>

              {/* Media Gallery */}
              <div className="glass-card p-6">
                <h2 className="text-xl font-display font-semibold text-white mb-4">Media</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {artist.media.map((m, i) => (
                    <div key={i} className="relative rounded-xl overflow-hidden group">
                      <img src={m} alt="media" className="w-full h-40 object-cover group-hover:scale-110 transition-transform duration-500" />
                      <button className="absolute inset-0 flex items-center justify-center text-white/90 opacity-0 group-hover:opacity-100 bg-black/30 transition-opacity duration-200">
                        <Play />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Upcoming Shows */}
              <div className="glass-card p-6">
                <h2 className="text-xl font-display font-semibold text-white mb-4">Upcoming Shows</h2>
                <div className="space-y-3">
                  {artist.upcoming.map((ev) => (
                    <div key={ev.id} className="flex items-center justify-between bg-white/5 rounded-xl p-4">
                      <div>
                        <div className="text-white font-medium">{ev.title}</div>
                        <div className="text-white/70 text-sm flex items-center gap-3 mt-1"><Calendar size={14} /> {ev.date} • {ev.venue}</div>
                      </div>
                      <button className="btn-secondary">Details</button>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default ProfilePage